// REG-PPP — PPP Arrangement Registry. Cross-cutting Class A registry (v2.0):
// one thin, type-classified registry for the whole FMLD PPP portfolio. Lives
// outside the eight statistical modules on purpose — most PPPs are not
// infrastructure, so it is not parked inside any single module's submodules.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FilterBar, SearchRow } from "../MarketTrad/shared";
import NlimsDataTable from "../shared/NlimsDataTable/NlimsDataTable";
import { useAuth } from "../../context/AuthContext";
import { pppArrangementsApi, PPP_TYPE_OPTIONS, PPP_ARRANGEMENT_MODEL_OPTIONS, PPP_STATUS_OPTIONS } from "../../api/pppArrangementsApi";
import { ActionDropdown, anchorRect, QuickConfirmModal, RejectNotesModal, WORKFLOW_RULES, actionCellHtml } from "../shared/RowActionMenu";

function getApiErrorMessage(error) {
  const response = error?.response?.data;
  const fieldErrors = response?.errors ?? response?.data?.errors;
  if (fieldErrors && typeof fieldErrors === "object") {
    const msgs = Object.entries(fieldErrors)
      .flatMap(([, v]) => (Array.isArray(v) ? v : [String(v)]))
      .filter(Boolean)
      .slice(0, 3);
    if (msgs.length) return msgs.join(" • ");
  }
  if (response?.message) return response.message;
  if (error?.message) return error.message;
  return "Something went wrong.";
}

function getRecordId(row) {
  return row?.uuid ?? row?.id ?? row?.raw?.uuid ?? row?.raw?.id ?? null;
}

function normalizeTextSearch(row = {}) {
  return Object.values(row)
    .filter((value) => typeof value === "string")
    .join(" ")
    .toLowerCase();
}

function usePlanningCollection(api) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const result = await api.listAll();
      const items = Array.isArray(result?.items) ? result.items : [];
      setRows(items);
      return items;
    } catch (err) {
      setRows([]);
      setError(getApiErrorMessage(err));
      throw err;
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void refresh().catch(() => {});
  }, [refresh]);

  const remove = useCallback(async (row) => {
    const uuid = getRecordId(row);
    if (!uuid) throw new Error("Missing record identifier.");
    setError("");
    try {
      const response = await api.remove(uuid);
      await refresh();
      return response;
    } catch (err) {
      setError(getApiErrorMessage(err));
      throw err;
    }
  }, [api, refresh]);

  const runAction = useCallback(async (row, method, ...args) => {
    const uuid = getRecordId(row);
    if (!uuid) throw new Error("Missing record identifier.");
    setError("");
    try {
      const result = await api[method](uuid, ...args);
      await refresh();
      return result;
    } catch (err) {
      setError(getApiErrorMessage(err));
      throw err;
    }
  }, [api, refresh]);

  return { rows, loading, error, refresh, remove, runAction };
}

const PPP_COLUMNS = [
  { title: "PPP ID", data: "pppArrangementId", sortable: true, defaultContent: "—" },
  { title: "Partner Organisation", data: "partnerOrgName", sortable: true, defaultContent: "—" },
  { title: "PPP Type", data: "pppTypeLabel", sortable: true },
  { title: "Arrangement Model", data: "arrangementModelLabel", sortable: true },
  { title: "Investment Value (NGN)", data: "investmentValueNgn", sortable: true },
  { title: "Commencement Date", data: "commencementDate", sortable: true },
  { title: "Expiry Date", data: "expiryDate", sortable: true },
  { title: "State", data: "state", sortable: true },
  { title: "Status", data: "statusLabel", sortable: true, render: (value, type) => (type === "display" ? `<span class="nlims-status nlims-status--${String(value ?? "").toLowerCase().replace(/\s+/g, "-")}">${value ?? ""}</span>` : value ?? "") },
  { title: "Data Status", data: "dataStatus", sortable: true, render: (value, type) => (type === "display" ? `<span class="nlims-status nlims-status--${String(value ?? "").toLowerCase().replace(/\s+/g, "-")}">${value ?? ""}</span>` : value ?? "") },
  { title: "Action", data: null, sortable: false, render: () => actionCellHtml() },
];

export default function PppArrangementsTab() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const roleId = user?.roleId ?? '';
  const canCreatePpp = roleId !== 'R02' && roleId !== 'R06' && roleId !== 'R05';
  const { rows, error, loading, remove, runAction } = usePlanningCollection(pppArrangementsApi);
  const [filters, setFilters] = useState({ pppType: "", arrangementModel: "", status: "" });
  const [search, setSearch] = useState("");
  const [actionMenu, setActionMenu] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [quickBusy, setQuickBusy] = useState(false);

  const pppFilters = useMemo(() => [
    { key: "pppType", label: "PPP Type", options: PPP_TYPE_OPTIONS.map((o) => o.label), defaultLabel: "All" },
    { key: "arrangementModel", label: "Arrangement Model", options: PPP_ARRANGEMENT_MODEL_OPTIONS.map((o) => o.label), defaultLabel: "All" },
    { key: "status", label: "Status", options: PPP_STATUS_OPTIONS.map((o) => o.label), defaultLabel: "All" },
  ], []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((row) => {
      const matchSearch = !q || normalizeTextSearch(row).includes(q);
      const matchType = !filters.pppType || row.pppTypeLabel === filters.pppType;
      const matchModel = !filters.arrangementModel || row.arrangementModelLabel === filters.arrangementModel;
      const matchStatus = !filters.status || row.statusLabel === filters.status;
      return matchSearch && matchType && matchModel && matchStatus;
    });
  }, [rows, filters, search]);

  const handleDelete = useCallback(async (payload) => {
    await remove(payload);
  }, [remove]);

  const handleRowAction = useCallback((action, row, meta) => {
    const rect = anchorRect(meta?.button);
    if (action === 'menu' || action == null) setActionMenu({ row, rect });
  }, []);

  const runQuickAction = useCallback(async (row, method) => {
    setQuickBusy(true);
    try { await runAction(row, method); }
    finally { setQuickBusy(false); }
  }, [runAction]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return;
    setQuickBusy(true);
    try { await handleDelete(deleteTarget); setDeleteTarget(null); }
    finally { setQuickBusy(false); }
  }, [deleteTarget, handleDelete]);

  const handleRejectConfirm = useCallback(async (notes) => {
    if (!rejectTarget) return;
    setQuickBusy(true);
    try { await runAction(rejectTarget, 'reject', notes); setRejectTarget(null); }
    finally { setQuickBusy(false); }
  }, [rejectTarget, runAction]);

  return (
    <div>
      <div className="mb-4 rounded-xl border border-indigo-100 bg-indigo-50/60 px-4 py-3 text-xs text-indigo-700">
        Cross-cutting registry — one thin, type-classified record per arrangement. Progress and milestones are reported via Activity Reports (activity_type = ppp_milestone), not here.
      </div>
      <FilterBar filters={pppFilters} filterValues={filters} onFilterChange={(key, value) => setFilters((prev) => ({ ...prev, [key]: value }))} onApply={() => {}} />
      <SearchRow searchValue={search} onSearch={setSearch} onCreate={() => navigate("/ppp-arrangements/new")} canCreate={canCreatePpp} />
      {loading && <div className="mb-4 text-xs text-gray-400">Loading live records...</div>}
      {error && <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
      <NlimsDataTable tableId="ppp-arrangements-table" columns={PPP_COLUMNS} data={filtered} serverSide={false} hideSearch pageLength={10} enableRowSelection onRowAction={handleRowAction} />
      {actionMenu && !quickBusy && (
        <ActionDropdown
          row={actionMenu.row}
          rect={actionMenu.rect}
          roleId={roleId}
          rules={WORKFLOW_RULES.pppArrangements}
          onClose={() => setActionMenu(null)}
          onView={() => {
            const row = actionMenu.row;
            setActionMenu(null);
            navigate(`/ppp-arrangements/view/${getRecordId(row)}`, { state: { row } });
          }}
          onEdit={() => {
            const row = actionMenu.row;
            setActionMenu(null);
            navigate(`/ppp-arrangements/edit/${getRecordId(row)}`, { state: { row } });
          }}
          onSubmit={() => runQuickAction(actionMenu.row, 'submit')}
          onValidate={() => runQuickAction(actionMenu.row, 'validate')}
          onApprove={() => runQuickAction(actionMenu.row, 'approve')}
          onReject={() => { setRejectTarget(actionMenu.row); setActionMenu(null); }}
          onDelete={() => setDeleteTarget(actionMenu.row)}
        />
      )}
      {rejectTarget && (
        <RejectNotesModal
          onConfirm={handleRejectConfirm}
          onClose={() => setRejectTarget(null)}
        />
      )}
      {deleteTarget && (
        <QuickConfirmModal
          title="Delete PPP Arrangement"
          message="Permanently delete this PPP arrangement? This cannot be undone."
          onConfirm={handleDeleteConfirm}
          onClose={() => setDeleteTarget(null)}
          busy={quickBusy}
        />
      )}
    </div>
  );
}
